import { getAuthToken } from '@providers/AuthProvider';
import axios from 'axios';
import { errorMessage } from './Constants';

const apiURL = process.env.REACT_APP_API_URL;
let activeRequestCount = 0;
const requestActivityListeners = new Set<(isLoading: boolean) => void>();

const notifyRequestActivityListeners = () => {
  const isLoading = activeRequestCount > 0;
  requestActivityListeners.forEach((listener) => listener(isLoading));
};

const startRequest = () => {
  activeRequestCount += 1;
  notifyRequestActivityListeners();
};

const endRequest = () => {
  activeRequestCount = Math.max(0, activeRequestCount - 1);
  notifyRequestActivityListeners();
};

export const subscribeToAxiosLoader = (
  listener: (isLoading: boolean) => void,
): (() => void) => {
  requestActivityListeners.add(listener);
  listener(activeRequestCount > 0);

  return () => {
    requestActivityListeners.delete(listener);
  };
};

export const Axios = axios.create({
  baseURL: apiURL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor to include the token dynamically
Axios.interceptors.request.use(
  (config) => {
    startRequest();
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    endRequest();
    return Promise.reject(error);
  },
);

// Add a response interceptor to handle responses globally
Axios.interceptors.response.use(
  (response) => {
    endRequest();
    return response;
  },
  (error) => {
    endRequest();
    if (error.status === 403) {
      // Handle 403 Forbidden error
      console.error(
        'Forbidden:',
        error.response?.data?.message || 'Access denied',
      );
      //remove the token from the local storage and redirect to login page
      localStorage.removeItem('authToken');
      window.location.href = '/login';
    } else if (error.status === 401) {
      // Handle 401 Unauthorized error
      console.error(
        'Unauthorized:',
        error.response?.data?.message || 'Authentication required',
      );
    }
    return Promise.reject(error);
  },
);

/**
 * Get the error message from an error object.
 *
 * @param {unknown} error - The error object.
 * @returns {string} - The error message.
 */
export const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.message ?? errorMessage.DEFAULT_ERROR_MESSAGE;
  } else if (error instanceof Error) {
    return error.message ?? errorMessage.DEFAULT_ERROR_MESSAGE;
  } else if (
    typeof error === 'object' &&
    error !== null &&
    'data' in error &&
    typeof (error as Record<string, unknown>).data === 'object'
  ) {
    return (
      (error as { data: { message?: string } }).data.message ??
      errorMessage.DEFAULT_ERROR_MESSAGE
    );
  } else {
    return errorMessage.DEFAULT_ERROR_MESSAGE;
  }
};
